// @flow
import React, {useState, useEffect, useMemo} from 'react';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {makeStyles, Typography, Checkbox, Divider} from '@material-ui/core';
import PropTypes from 'prop-types';
import SearchBar from '../../../../../elements/SearchBar/SearchBar';
import CustomButtom from '../../../../../elements/CustomButton/CustomButton';
import {useCountries} from '../../../../../modules/country/utils';
import type {Country} from '../../../../../entities/country';
import {useUpdateShippingCountries} from '../utils';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  searchBar: {
    backgroundColor: 'transparent',
    fontSize: '10rem',
    width: '100%',

    '& input': {
      fontSize: '10rem',
    },
  },
  alert: {
    width: '100%',
    margin: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxHeight: 450,
    overflowY: 'auto',

    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#ccc',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#b3b3b3',
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  leftContent: {
    display: 'flex',
    alignItems: 'start',
  },
  divider: {
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  adm3Name: {
    cursor: 'pointer',
  },
}));

type Props = {
  open: boolean,
  country?: Country,
  edit?: boolean,
  handleClose: () => void,
  availableShippingCountries: Country[],
};

export default function InternationalCountriesModal(props: Props) {
  const {open, country, edit, handleClose, availableShippingCountries} = props;
  const classes = useStyles();

  const countries = useCountries();
  const dataCountriesLength = countries?.filter(
    countryG => countryG.code !== country?.code
  ).length;
  const [dataCountries, setDataCountries] = useState<Country[]>([]);
  const [countriesSelected, setCountriesSelected] = useState([]);
  const {updateShippingCountries} = useUpdateShippingCountries();

  const countriesFilter = useMemo(() => {
    return (
      country && countries.filter(countryG => countryG.code !== country.code)
    );
  }, [country, countries]);

  const handleCloseModal = () => {
    handleClose();
  };

  const handleToggleCountry = (e, country) => {
    e.stopPropagation();
    const countryIndex = countriesSelected.findIndex(
      admin => admin.id === country.id
    );
    if (countryIndex === -1) {
      countriesSelected.push({...country, already_used: true});
    } else {
      countriesSelected.splice(countryIndex, 1);
    }
    setCountriesSelected([...countriesSelected]);
  };

  const handleSaveCountries = () => {
    const countriesSelectedFormatted: string[] = countriesSelected.map(
      country => String(country.id)
    );
    updateShippingCountries(countriesSelectedFormatted);
    handleCloseModal();
  };

  const handleSearch = (searchString: string) => {
    if (searchString === '') {
      // $FlowFixMe
      setDataCountries(countriesFilter);
      return;
    }

    const searchStringLower = searchString.toLowerCase();
    const filteredCountries =
      countriesFilter &&
      countriesFilter.filter(countryG => {
        if (countryG.name?.toLowerCase().includes(searchStringLower)) {
          return true;
        }
        return (
          countryG.code.toLowerCase() === searchStringLower &&
          countryG.code !== country?.code
        );
      });

    // $FlowFixMe
    setDataCountries(filteredCountries);
  };

  const isChecked = country => {
    return countriesSelected.some(
      admin => admin.id === country.id && admin.already_used
    );
  };

  useEffect(() => {
    if (countries && country) {
      // $FlowFixMe
      setDataCountries(countriesFilter);
    }
  }, [countries, countriesFilter, country]);

  useEffect(() => {
    if (countries && availableShippingCountries && country) {
      const selectedCountries = availableShippingCountries
        .filter(countryAvailable => countryAvailable.code !== country?.code)
        .map(countryAvailable => {
          return {
            ...countryAvailable,
            already_used: true,
          };
        });
      setCountriesSelected(selectedCountries);
    }
  }, [availableShippingCountries, countries, country]);

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        {edit ? (
          <Typography align="center" gutterBottom className={classes.title}>
            Editar Paises
          </Typography>
        ) : (
          <Typography align="center" gutterBottom className={classes.title}>
            Seleccionar Paises
          </Typography>
        )}
        {edit && (
          <Alert severity="warning" className={classes.alert}>
            Al remover un país, las tarifas asociadas a ese país serán
            desactivadas.
          </Alert>
        )}
        <SearchBar
          placeholder="Buscar paises por nombre o código de área"
          className={classes.searchBar}
          color="#fff"
          maxWidth={600}
          border="1px solid rgba(232, 232, 237, 1)"
          borderRadius="8px"
          externalHandleSearch={(e: any) => handleSearch(e)}
        />
        <div className={classes.container}>
          {dataCountries &&
            dataCountries.map((country: Country) => (
              <div key={country.id} className={classes.row}>
                <Checkbox
                  id={`checkbox-${country.code}`}
                  color="primary"
                  className={classes.adm3Checkbox}
                  checked={isChecked(country)}
                  onClick={e => {
                    handleToggleCountry(e, country);
                  }}
                />
                <label
                  className={classes.adm3Name}
                  htmlFor={`checkbox-${country.code}`}
                >
                  {country.name}
                </label>
              </div>
            ))}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.actions}>
          <Typography>
            {countriesSelected.filter(country => country.already_used).length}{' '}
            de {dataCountriesLength} paises seleccionados
          </Typography>
          <CustomButtom
            variant="contained"
            color="primary"
            onClick={handleSaveCountries}
          >
            Confirmar
          </CustomButtom>
        </div>
      </div>
    </CustomModal>
  );
}

InternationalCountriesModal.propTypes = {
  open: PropTypes.bool,
  country: PropTypes.object,
  edit: PropTypes.bool,
  handleClose: PropTypes.func,
  availableShippingCountries: PropTypes.array,
};
